import { AbstractControl } from '@angular/forms';
import dayjs from 'dayjs';
import { initialCaps, sleep } from './utilities';
import { CalculateFutureDatePayload } from '@approot/models';

const isCorrectFullDateFormatRe = new RegExp(
  '(^([1-9]|[12][0-9]|3[01])[\\s](jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)([\\s][0-9]{4})$)|(^[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])$)|^(0[1-9]|[1-2][0-9]|3[0-1])/(0[1-9]|1[0-2])/[0-9]{4}$',
  'i'
);
const isCorrectDateFormatRe = new RegExp(
  '(^([1-9]|[12][0-9]|3[01])[\\s](jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)([\\s][0-9]{4})?$)|(^[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])$)|^(0[1-9]|[1-2][0-9]|3[0-1])/(0[1-9]|1[0-2])/[0-9]{4}$',
  'i'
);
const isPrettyDateRe =
  /^([1-9]|[12][0-9]|3[01])\s(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)(\s[0-9]{4})$/i;
const isDateOfSlashFormatRe = new RegExp(
  '^(0[1-9]|[1-2][0-9]|3[0-1])/(0[1-9]|1[0-2])/[0-9]{4}$'
);
const isDateOfDashedFormatRe =
  /^[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])$/;
const isValidLagRe = /^[-+]?\d+[bdwmy]$/;

export class DateUtilities {
  // converts DD/MM/YYYY to YYYY-MM-DD
  static restructureDate(dateString: string) {
    return dateString.split('/').reverse().join('-');
  }

  // converts YYYY-MM-DD to DD/MM/YYYY
  private static restructureDateForControl(dateString: string): string {
    return dateString.split('-').reverse().join('/');
  }

  static getDateInISOFormat(
    indate?: Date | string | number,
    throwError = true
  ): string {
    // takes a date and converts it into YYYY-MM-DD
    let date: Date;
    if (indate === undefined) {
      date = new Date();
    } else if (typeof indate === 'string' || typeof indate === 'number') {
      date = new Date(indate);
    } else {
      date = indate;
    }
    try {
      return (
        new Date(date.getTime() - date.getTimezoneOffset() * 60000)
          // converts date to a date that corresponds to the date at 00::00::00 in UTC
          .toISOString() // converts string to YYYYY-MM-DDT00:00:00.000Z
          .split('T')[0]
      ); // split at T and take first of split
    } catch (e) {
      if (throwError) {
        // 1. Default error message doesn't show the "date" that caused the error
        // 2. When NG updates to ES2022, can add 2nd argument {cause: e}
        throw new Error(`Date is not valid: <${indate}>`);
      } else {
        return `${indate}`;
      }
    }
  }

  // returns date in format of 2020-10-10. note if not convertible it will return the request
  static getDateInDashedFormat(date: string) {
    return reformatDate(date, 'YYYY-MM-DD');
  }

  static isCorrectDateFormat(value: string) {
    // is the date of format
    // 12 jan or 12 jan 2020 or 2020-03-31 or 15/05/2020
    return isCorrectDateFormatRe.test(value);
  }

  static isCorrectFullDateFormat(value: string) {
    return isCorrectFullDateFormatRe.test(value);
  }

  static isPrettyDate(date: string) {
    return isPrettyDateRe.test(date);
  }

  static isValidLag(value: string) {
    // is it just a lag e.g. 12m
    if (typeof value !== 'string') {
      return false;
    }
    value = value.toLowerCase().trim();
    return isValidLagRe.test(value);
  }

  static isDateOfSlashFormat(value: string) {
    // is date of format 14/04/2020
    return isDateOfSlashFormatRe.test(value);
  }

  static isDateOfDashedFormat(value: string) {
    // is date of format yyyy-mm-dd
    return isDateOfDashedFormatRe.test(value);
  }

  static dateToPrettyDate(date: Date | string | number) {
    return reformatDate(date, 'D MMM YYYY');
  }

  static prettyDateToDate(date: string) {
    // 20 mar 2020 => 2020-12-25
    return this.getDateInISOFormat(date);
  }

  static prettyDateToSlashedDate(date: string) {
    // 20 mar 2020 => 15/12/2020
    return this.restructureDateForControl(this.getDateInISOFormat(date));
  }

  static dateOrLagToPayload(
    dateOrLag: string,
    cross: string,
    refDate: string,
    cutOff?: string,
    fixingSource?: string
  ): CalculateFutureDatePayload {
    // function returns a payload for use of the expiryDate function
    if (this.isDateOfSlashFormat(dateOrLag)) {
      dateOrLag = this.restructureDate(dateOrLag);
      // if the date is of format mm/dd/yyyy this isn't something that can be handled in the validation
    } else if (this.isPrettyDate(dateOrLag)) {
      dateOrLag = this.prettyDateToDate(dateOrLag);
    }
    let payload: CalculateFutureDatePayload = {
      referenceDate: refDate ? refDate : this.getDateInISOFormat(), // get the current date in ISO format
      dateOrLag,
      cutOff,
      fixingSource
    };

    if (cross.length === 3) {
      payload['currency'] = cross;
    } else {
      payload['currencyPair'] = cross;
    }
    return payload;
  }

  static dateComparator(
    date1: number | string | Date,
    date2: number | string | Date
  ) {
    if (date1 == null && date2 == null) {
      return 0;
    }

    if (date1 == null) {
      return -1;
    } else if (date2 == null) {
      return 1;
    }
    const date1Number = new Date(date1).getTime();
    const date2Number = new Date(date2).getTime();

    return date1Number - date2Number;
  }

  static filterDateComparator(
    filterLocalDateAtMidnight: Date,
    cellValue: string
  ) {
    const dateAsString = cellValue;

    if (dateAsString == null) {
      return 0;
    }

    // In the example application, dates are stored as dd/mm/yyyy
    // We create a Date object for comparison against the filter date
    const dateParts = dateAsString.split('-');
    const day = Number(dateParts[2]);
    const month = Number(dateParts[1]) - 1;
    const year = Number(dateParts[0]);
    const cellDate = new Date(year, month, day);

    // Now that both parameters are Date objects, we can compare
    if (cellDate < filterLocalDateAtMidnight) {
      return -1;
    } else if (cellDate > filterLocalDateAtMidnight) {
      return 1;
    }
    return 0;
  }

  static isDateInPast = (firstDate: Date) => {
    if (firstDate.setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0) > 0) {
      //first date is in future, or it is today
      return false;
    }

    return true;
  };

  static async onDateChanged(formElem: AbstractControl): Promise<void> {
    const val = formElem.value;
    if (!(val && this.isDateOfSlashFormat(val))) {
      return;
    }
    await sleep(5);
    const newDate = this.dateToPrettyDate(this.restructureDate(val));
    formElem.patchValue(newDate, { emitEvent: false });
  }
}

// dayjs will attempt to parse dates using these formats.  We need to be so explicit
// because dayjs (like Date) will parse 1/2/2000 using US style MM/DD/YYYY even if
// we load the en-gb locale.
export type DateFormat =
  | 'DD MMM YYYY'
  | 'D MMM YYYY'
  | 'D/M/YYYY'
  | 'D/MM/YYYY'
  | 'DD/M/YYYY'
  | 'DD/MM/YYYY'
  | 'YYYY-MM-DD';

const DATE_FORMATS: DateFormat[] = [
  'DD MMM YYYY',
  'D MMM YYYY',
  'D/M/YYYY',
  'D/MM/YYYY',
  'DD/M/YYYY',
  'DD/MM/YYYY',
  'YYYY-MM-DD'
];

/**
 * takes a date (in a predefined string format) and returns it in the
 * requested format (using dayjs format specification).
 *
 * e.g., reformatDate('2020-12-25', 'DD MMM YYYY') => '25 Dec 2020'
 *
 * reformatDate(d, fmt) will attempt to automatically detect the format and output in fmt
 * reformatDate(d, {in: infmt, out: outfmt}) will attempt to convert infmt -> outfmt.  infmt can be an array of formats
 *
 * The latter will be faster if you already know the format of the input date but
 * bear in mind that dayjs doesn't handle double vs. single digits very well
 *
 */

type DateFormatSpec = {
  in?: DateFormat | DateFormat[];
  out: DateFormat;
  throwOnError?: boolean;
  strict?: boolean;
};

export function reformatDate(
  inDate: string | Date | number,
  format: DateFormat | DateFormatSpec
): string {
  if (inDate == null) {
    return null;
  }
  let inFormats: DateFormat[] | DateFormat;
  let outFormat: DateFormat;
  let throwOnError: boolean;
  let strict: boolean;

  if (typeof inDate === 'string') {
    /* dates where the month starts with a lower
    case are seen as invalid e.g. 22 mar 2022 */
    inDate = initialCaps(inDate.trim());
  }

  if (typeof format === 'string') {
    inFormats = DATE_FORMATS;
    outFormat = format;
    throwOnError = false;
    strict = false;
  } else {
    inFormats = format.in || DATE_FORMATS;
    outFormat = format.out;
    throwOnError = format.throwOnError;
    strict = format.strict;
  }
  if (outFormat == null) {
    throw new Error('No output format specified');
  }

  // If the argument is already a (JS) Date or number, then just format it.
  if (inDate instanceof Date || typeof inDate === 'number') {
    return dayjs(inDate).format(outFormat);
  }
  const date = dayjs(inDate, inFormats, strict);
  if (date.isValid()) {
    return date.format(outFormat);
  }
  if (throwOnError) {
    throw new Error(`Invalid date ${inDate}`);
  } else {
    return inDate;
  }
}

/**
 * We use the strict flag to disallow, e.g., '30 Feb yyyy'
 */
export function isValidDate(
  inDate: string,
  format?: DateFormat | DateFormat[],
  strict?: boolean
): boolean {
  if (format === undefined) {
    format = DATE_FORMATS;
  }
  strict = strict === undefined ? true : strict;
  return dayjs(inDate, format, strict).isValid();
}

export function timeUntilLocalMidnight(): number {
  const midnight = dayjs().startOf('day').add(1, 'day');
  const tz = dayjs.tz.guess();
  const midnightTz = dayjs.tz(midnight, tz);
  console.log(
    'Will logoff at midnight, roughly',
    midnightTz.fromNow(),
    'from now'
  );

  const midnightMs = midnightTz.valueOf();
  return midnightMs - Date.now();
}
